if (typeof customElements.get('rx-method-block') === 'undefined') {

	class RxMethodBlock extends HTMLElement {

		constructor() {
			super();

			this.radios = Array.from(this.querySelectorAll('[data-rx-radio]'));
			this.doctorName = this.querySelector('[data-rx-doctor-name]');
			this.doctorPhone = this.querySelector('[data-rx-doctor-phone]');

			this.radios.forEach((radio) => {
				radio.addEventListener('change', () => this.onMethodChange());
			});

			if (this.doctorName) {
				this.doctorName.addEventListener('input', () => this.onFieldsChange());
			}
			if (this.doctorPhone) {
				this.doctorPhone.addEventListener('input', () => this.onFieldsChange());
			}

			// Checkout-state evaluation (blocked/termsOk/disabled) now lives in the free function
			// window.PBLA_updateRxCheckoutState below, not as a method here — see its own comment for why.
			this.updateVisibility();
			window.PBLA_updateRxCheckoutState();
		}

		get selected() {
			const checked = this.radios.find((radio) => radio.checked);
			return checked ? checked.value : '';
		}

		onMethodChange() {
			this.updateVisibility();
			window.PBLA_updateRxCheckoutState();
			this.persist();
		}

		onFieldsChange() {
			window.PBLA_updateRxCheckoutState();
			this.persistDebounced();
		}

		updateVisibility() {
			const method = this.selected;

			this.querySelectorAll('[data-rx-card]').forEach((card) => {
				card.classList.toggle('is-selected', card.dataset.rxCard === method);
			});

			const fields = this.querySelector('[data-rx-doctor-fields]');
			const isContactPrescriber = method === 'contact_prescriber';
			if (fields) {
				fields.classList.toggle('is-visible', isContactPrescriber);
			}
			if (this.doctorName) {
				this.doctorName.required = isContactPrescriber;
			}
			if (this.doctorPhone) {
				this.doctorPhone.required = isContactPrescriber;
			}

			const isVisit = method === 'visit';
			const note = this.querySelector('[data-rx-visit-note]');
			const cta = this.querySelector('[data-rx-visit-cta]');
			if (note) note.classList.toggle('is-visible', isVisit);
			if (cta) cta.classList.toggle('is-visible', isVisit);

			// PBLA (sep 2026): picking "I don't have a prescription yet" means no order is placed now — the
			// terms checkbox and the Checkout button (+ its hint) are pointless in that state, so hide them.
			// They come back the moment another card is picked. Purely visual: window.PBLA_updateRxCheckoutState
			// still keeps #CheckOut disabled regardless, this just stops showing controls that can't be used.
			// Liquid pre-renders the same class server-side (cart-subtotal.liquid, pbla_rx_visit_selected)
			// so a page load that already has rx_method=visit saved doesn't flash them before this runs.
			document.querySelectorAll('[data-rx-hide-on-visit]').forEach((el) => {
				el.classList.toggle('pbla-rx-hidden', isVisit);
			});
		}

		persistDebounced() {
			clearTimeout(this._persistTimer);
			this._persistTimer = setTimeout(() => this.persist(), 400);
		}

		persist() {
			const method = this.selected;
			const isContactPrescriber = method === 'contact_prescriber';

			const body = JSON.stringify({
				attributes: {
					rx_method: method,
					rx_doctor_name: isContactPrescriber && this.doctorName ? this.doctorName.value.trim() : '',
					rx_doctor_phone: isContactPrescriber && this.doctorPhone ? this.doctorPhone.value.trim() : ''
				}
			});

			const url = (window.KROWN && KROWN.settings && KROWN.settings.routes && KROWN.settings.routes.cart_update_url)
				? `${KROWN.settings.routes.cart_update_url}.js`
				: '/cart/update.js';

			fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body
			}).catch(() => {});
		}

	}

	customElements.define('rx-method-block', RxMethodBlock);

}

if (typeof window.PBLA_updateRxCheckoutState !== 'function') {
	// PBLA (sep 2026, bug fix): single source of truth for the /cart Checkout gate — combines the selected
	// Rx method's own completeness (blocked) with the terms checkbox (termsOk). A free function, not a
	// RxMethodBlock method, specifically so a delegated listener on `document` (registered once, below) can
	// call it and be guaranteed to run AFTER assets/component-terms-checkbox.js's own listener, instead of
	// racing with it via listener-registration order.
	//
	// Root cause of the bug this replaces: <terms-checkbox> (component-terms-checkbox.js) also listens for
	// 'change' directly on #AgreeToTos and unconditionally sets `button.disabled = !checkbox.checked`, with
	// zero awareness of the Rx gate. On a fresh page load its script executes first (defer scripts run in
	// document order, and its <script src> — emitted inline by cart-subtotal.liquid — sits earlier in the
	// page than this file's own <script> in layout/theme.liquid), so it attached its listener first and the
	// old RxMethodBlock listener (attached directly on the checkbox too) ran second and correctly won. But
	// every AJAX cart re-render (window.renderHelperCart replacing #AjaxCartSubtotal's innerHTML) constructs
	// BOTH custom elements fresh and upgrades them in DOM tree order instead of script-load order —
	// <rx-method-block> sits earlier in the markup than <terms-checkbox> (cart-subtotal.liquid), so after
	// any re-render the attach order flips: terms-checkbox's naive listener ends up LAST and wins, silently
	// re-enabling Checkout on any checkbox toggle regardless of the selected Rx method. Reproduced directly:
	// pick "We'll contact your prescriber" with no doctor info, then uncheck/recheck the terms checkbox —
	// Checkout re-enabled.
	//
	// Fix: never attach directly on the checkbox — listen on `document` instead (delegated, bubble phase).
	// Per the DOM event spec, an ancestor's bubble-phase listener always runs after a listener attached
	// directly on the event's target, unconditionally, regardless of registration order — so this always
	// evaluates last and wins, no matter which custom element happened to upgrade first. There is exactly
	// one <rx-method-block> on /cart, so this reads its live state straight off the DOM each time rather
	// than keeping an instance reference that an AJAX re-render would make stale.
	window.PBLA_updateRxCheckoutState = function () {
		const block = document.querySelector('rx-method-block[data-rx-method-block]');
		const checkoutBtn = document.getElementById('CheckOut');
		const hint = document.querySelector('[data-rx-checkout-hint]');
		if (!block || !checkoutBtn) return;

		const radios = Array.from(block.querySelectorAll('[data-rx-radio]'));
		const checked = radios.find((radio) => radio.checked);
		const method = checked ? checked.value : '';

		let blocked = true;
		let hintText = 'Choose an option above to continue';

		if (method === 'send') {
			blocked = false;
			hintText = '';
		} else if (method === 'contact_prescriber') {
			const doctorName = block.querySelector('[data-rx-doctor-name]');
			const doctorPhone = block.querySelector('[data-rx-doctor-phone]');
			const name = doctorName ? doctorName.value.trim() : '';
			const phone = doctorPhone ? doctorPhone.value.trim() : '';
			if (name && phone) {
				blocked = false;
				hintText = '';
			} else {
				hintText = "Complete your doctor's details to continue";
			}
		} else if (method === 'visit') {
			hintText = 'Book the free visit to continue';
		}

		const termsCheckbox = document.getElementById('AgreeToTos');
		const termsOk = !termsCheckbox || termsCheckbox.checked;

		checkoutBtn.disabled = blocked || !termsOk;
		if (hint) {
			// hintText already holds '' for both valid-and-unblocked cases (send, or contact_prescriber with
			// both fields) — termsOk never affects the hint text, only .disabled above.
			hint.textContent = hintText;
		}
	};

	// Registered once (this whole file only loads once per page load — see layout/theme.liquid's
	// `template.name contains "cart"` gate), not per RxMethodBlock instance, so it survives every AJAX
	// re-render without piling up duplicate listeners.
	document.addEventListener('change', (event) => {
		if (event.target && event.target.id === 'AgreeToTos') {
			window.PBLA_updateRxCheckoutState();
		}
	});
}

if (typeof window.PBLA_checkRxStaleAttrs !== 'function') {
	// PBLA: clears rx_method/rx_doctor_name/rx_doctor_phone from the cart when the cart no longer has a
	// flow:rx item but those attributes are still set (see the [data-rx-attrs-stale] marker rendered by
	// snippets/cart-subtotal.liquid). Defined here — not inline where the marker lives — because that
	// snippet's HTML is routinely injected via innerHTML (assets/component-cart.js's renderHelperCart),
	// which never executes injected <script> tags. Called once below (covers landing on a page that's
	// already stale) and again from renderHelperCart after every AJAX cart re-render.
	window.PBLA_checkRxStaleAttrs = function () {
		if (!document.querySelector('[data-rx-attrs-stale]')) return;

		const url = (window.KROWN && KROWN.settings && KROWN.settings.routes && KROWN.settings.routes.cart_update_url)
			? `${KROWN.settings.routes.cart_update_url}.js`
			: '/cart/update.js';

		fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ attributes: { rx_method: '', rx_doctor_name: '', rx_doctor_phone: '' } })
		}).catch(() => {});
	};

	window.PBLA_checkRxStaleAttrs();
}
